import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Megaphone, Sparkles, X } from "lucide-react";
import { fetchNotifications } from "../lib/cmsApi";

const DISMISS_KEY = "apexbrain_dismissed_notifications";

function readDismissed() {
    try {
        const raw = localStorage.getItem(DISMISS_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
        return [];
    }
}

function dismissId(id) {
    const next = Array.from(new Set([...readDismissed(), String(id)]));
    localStorage.setItem(DISMISS_KEY, JSON.stringify(next));
}

const STYLE = {
    info: {
        bar: "border-cyan-400/20 bg-gradient-to-r from-cyan-500/10 via-obsidian-200/90 to-obsidian-200/90",
        dot: "bg-cyan-300",
        label: "text-cyan-300",
        cta: "text-cyan-200 hover:text-white",
    },
    success: {
        bar: "border-emerald-400/20 bg-gradient-to-r from-emerald-500/10 via-obsidian-200/90 to-obsidian-200/90",
        dot: "bg-emerald-300",
        label: "text-emerald-300",
        cta: "text-emerald-200 hover:text-white",
    },
    warning: {
        bar: "border-amber-400/20 bg-gradient-to-r from-amber-500/10 via-obsidian-200/90 to-obsidian-200/90",
        dot: "bg-amber-glow",
        label: "text-amber-glow",
        cta: "text-amber-200 hover:text-white",
    },
    promo: {
        bar: "border-violet-400/20 bg-gradient-to-r from-violet-500/10 via-obsidian-200/90 to-obsidian-200/90",
        dot: "bg-violet-300",
        label: "text-violet-300",
        cta: "text-violet-200 hover:text-white",
    },
};

function styleFor(key) {
    return STYLE[key] || STYLE.info;
}

/** Sets CSS variable used by Nav for top offset. */
export function useSiteNotifications() {
    const [items, setItems] = useState([]);
    const [dismissed, setDismissed] = useState(readDismissed);
    const [popupId, setPopupId] = useState(null);

    useEffect(() => {
        fetchNotifications()
            .then((data) => setItems(data?.notifications ?? []))
            .catch(() => setItems([]));
    }, []);

    const visible = useMemo(
        () => items.filter((item) => !dismissed.includes(String(item.id))),
        [items, dismissed],
    );

    const banner = visible.find((item) => item.notification_type === "banner") ?? null;
    const popups = visible.filter((item) => item.notification_type === "popup");

    useEffect(() => {
        if (popups.length === 0) {
            setPopupId(null);
            return;
        }
        if (!popupId || !popups.some((p) => String(p.id) === String(popupId))) {
            setPopupId(popups[0].id);
        }
    }, [popups, popupId]);

    useEffect(() => {
        document.documentElement.style.setProperty(
            "--site-announcement-h",
            banner ? "44px" : "0px",
        );
        return () => {
            document.documentElement.style.setProperty("--site-announcement-h", "0px");
        };
    }, [banner]);

    function handleDismiss(id) {
        dismissId(id);
        setDismissed(readDismissed());
    }

    const popup = popups.find((item) => String(item.id) === String(popupId));

    return { banner, popup, handleDismiss };
}

export function SiteAnnouncementBar({ banner, onDismiss }) {
    if (!banner) return null;

    const theme = styleFor(banner.style);
    const message = String(banner.message ?? "").trim();

    return (
        <>
            <div className="h-[var(--site-announcement-h,0px)] shrink-0" aria-hidden />
            <div
                className={`fixed top-0 inset-x-0 z-[60] border-b backdrop-blur-md ${theme.bar}`}
                data-testid="site-announcement-bar"
                role="status"
                aria-live="polite"
            >
                <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2.5 sm:px-8">
                    <span className="hidden sm:inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5">
                        <Megaphone className={`h-3 w-3 ${theme.label}`} />
                        <span className={`font-code text-[10px] uppercase tracking-[0.18em] ${theme.label}`}>
                            Update
                        </span>
                    </span>

                    <span className={`mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full ${theme.dot} animate-pulse-glow sm:hidden`} />

                    <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-zinc-100">
                            <span className="font-semibold text-white">{banner.title}</span>
                            {message ? (
                                <>
                                    <span className="mx-2 hidden text-zinc-600 sm:inline">·</span>
                                    <span className="hidden text-zinc-400 sm:inline">{message}</span>
                                </>
                            ) : null}
                        </p>
                        {message ? (
                            <p className="mt-0.5 truncate text-xs text-zinc-500 sm:hidden">{message}</p>
                        ) : null}
                    </div>

                    {banner.cta_url && banner.cta_label ? (
                        <a
                            href={banner.cta_url} target="_blank" rel="noopener noreferrer"
                            className={`hidden shrink-0 items-center gap-1 text-xs font-semibold transition sm:inline-flex ${theme.cta}`}
                        >
                            {banner.cta_label}
                            <ArrowUpRight className="h-3.5 w-3.5" />
                        </a>
                    ) : null}

                    {banner.dismissible !== false ? (
                        <button
                            type="button"
                            aria-label="Dismiss announcement"
                            onClick={() => onDismiss(banner.id)}
                            className="shrink-0 rounded-md p-1.5 text-zinc-500 transition hover:bg-white/10 hover:text-white"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    ) : null}
                </div>
            </div>
        </>
    );
}

export function SiteNotificationPopup({ popup, onDismiss }) {
    if (!popup) return null;

    const theme = styleFor(popup.style);

    return (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/55 p-4 backdrop-blur-sm sm:items-center">
            <div
                className="relative w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-obsidian-200 shadow-2xl"
                role="dialog"
                aria-modal="true"
                aria-labelledby="site-notification-title"
            >
                <div className={`h-1 w-full bg-gradient-to-r ${theme.bar.includes("cyan") ? "from-cyan-400 to-emerald-400" : "from-violet-400 to-electric"}`} />
                <div className="p-6">
                    {popup.dismissible !== false ? (
                        <button
                            type="button"
                            aria-label="Close notification"
                            onClick={() => onDismiss(popup.id)}
                            className="absolute right-4 top-4 rounded-md p-1 text-zinc-500 hover:bg-white/10 hover:text-white"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    ) : null}
                    <div className="flex items-center gap-2">
                        <Sparkles className={`h-4 w-4 ${theme.label}`} />
                        <p className={`font-code text-[10px] uppercase tracking-[0.2em] ${theme.label}`}>
                            Announcement
                        </p>
                    </div>
                    <h2 id="site-notification-title" className="mt-3 font-outfit text-xl font-semibold text-white">
                        {popup.title}
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-zinc-400">{popup.message}</p>
                    {popup.cta_url && popup.cta_label ? (
                        <a
                            href={popup.cta_url}
                            className="mt-5 inline-flex items-center gap-2 rounded-full bg-gradient-to-b from-electric to-electric-deep px-5 py-2.5 text-sm font-semibold text-white transition hover:brightness-110"
                        >
                            {popup.cta_label}
                            <ArrowUpRight className="h-4 w-4" />
                        </a>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export function SiteNotifications() {
    const { banner, popup, handleDismiss } = useSiteNotifications();

    return (
        <>
            <SiteAnnouncementBar banner={banner} onDismiss={handleDismiss} />
            <SiteNotificationPopup popup={popup} onDismiss={handleDismiss} />
        </>
    );
}
